import {
  IPayableSchema,
  IPayrollSettings,
  ScheduleStatus
} from "@wingspanhq/payments/dist/interfaces";
import { wsMoment } from "@wingspanhq/utils/dist/date/wsMoment";

const UTC_PAYROLL_CUTOFF_HOUR = 17;
const UTC_PAYROLL_CUTOFF_MINUTES = 30;

// A past-cutoff Pending schedule is "currently processing" only if its cutoff
// happened within this window. Anything older is treated as stuck data — most
// often an off-cycle run or a Pending entry the backend never transitioned to
// Completed. Without this bound, stale schedule entries leak into the UI as
// "Expected pay date: <months ago>" (PLA-2734, PLA-2741).
const MAX_PROCESSING_AGE_BUSINESS_DAYS = 5;

export const getExactCutOffDate = (cutOffDate: Date) => {
  return new Date(
    Date.UTC(
      cutOffDate.getUTCFullYear(),
      cutOffDate.getUTCMonth(),
      cutOffDate.getUTCDate(),
      UTC_PAYROLL_CUTOFF_HOUR,
      UTC_PAYROLL_CUTOFF_MINUTES
    )
  );
};

export const getNextPayroll = (payrollSettings: IPayrollSettings) =>
  payrollSettings.scheduleDates?.find(scheduleDate =>
    (scheduleDate.status === ScheduleStatus.Pending ||
      scheduleDate.status === ScheduleStatus.Modified) &&
    scheduleDate?.cutOffDate
      ? getExactCutOffDate(scheduleDate.cutOffDate) > new Date()
      : false
  );

export const getNextPayrollCutOffDate = (payrollSettings: IPayrollSettings) =>
  getNextPayroll(payrollSettings)?.cutOffDate;

/**
 * Finds a payroll that is still Pending but whose cutoff has already passed,
 * meaning it's currently being processed (queued). Falls back to the next
 * upcoming payroll if none is currently processing.
 */
export const getCurrentOrNextPayroll = (payrollSettings: IPayrollSettings) => {
  const now = new Date();

  const pendingSchedules = payrollSettings.scheduleDates?.filter(
    scheduleDate =>
      (scheduleDate.status === ScheduleStatus.Pending ||
        scheduleDate.status === ScheduleStatus.Modified) &&
      scheduleDate?.cutOffDate
  );

  const next = pendingSchedules?.find(
    s => getExactCutOffDate(s.cutOffDate!) > now
  );

  // Last pending schedule whose cutoff has passed — the most recent processing run.
  // Two guards keep stale data out:
  //   1. There must be an upcoming schedule. If everything is past-cutoff, the
  //      whole array is likely stuck.
  //   2. The cutoff must be within MAX_PROCESSING_AGE_BUSINESS_DAYS of now.
  //      An older past-cutoff Pending is stuck (off-cycle leftover or BE never
  //      transitioned to Completed), not actually processing.
  const earliestProcessingCutoff = wsMoment
    .utc(now)
    .businessSubtract(MAX_PROCESSING_AGE_BUSINESS_DAYS)
    .toDate();

  const currentlyProcessing = next
    ? pendingSchedules
        ?.filter(s => {
          const cutoff = getExactCutOffDate(s.cutOffDate!);
          return cutoff <= now && cutoff >= earliestProcessingCutoff;
        })
        .pop()
    : undefined;

  return currentlyProcessing ?? next;
};

/**
 * Finds the payroll schedule that will include a given payable.
 *
 * When enableProcessDaysBeforeDue is OFF, the backend applies no date filter —
 * all approved invoices go into the next run. So we return getCurrentOrNextPayroll.
 *
 * When ON, mirrors the backend's bufferedCheckDate logic:
 *   bufferedCheckDate = scheduleDate.date + processDaysBeforeDue + 1 business day
 * A payable belongs to the first pending schedule whose bufferedCheckDate
 * exceeds the payable's effective due date (client.payDate || dueDate).
 */
export const getMatchingPayroll = (
  payrollSettings: IPayrollSettings,
  payable: IPayableSchema
) => {
  if (!payrollSettings.enableProcessDaysBeforeDue) {
    return getCurrentOrNextPayroll(payrollSettings);
  }

  const effectiveDueDate = payable.client.payDate || payable.dueDate;

  const processDaysBeforeDue = payrollSettings.processDaysBeforeDue ?? 0;

  return payrollSettings.scheduleDates?.find(scheduleDate => {
    if (
      scheduleDate.status !== ScheduleStatus.Pending &&
      scheduleDate.status !== ScheduleStatus.Modified
    ) {
      return false;
    }

    const bufferedCheckDate = wsMoment
      .utc(scheduleDate.date)
      .businessAdd(processDaysBeforeDue + 1)
      .toDate();

    return effectiveDueDate < bufferedCheckDate;
  });
};
